import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Home, ChefHat, CalendarDays, History, Settings, Shield, LogOut, Menu, User, Sparkles, MessageSquare, Dumbbell } from 'lucide-react';
import Tutorial from './Tutorial';

export default function Layout({ children }) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [runTutorial, setRunTutorial] = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    if (user.has_seen_tutorial) return;

    const pageMapping = {
      '/dashboard': 'dashboard',
      '/recipes': 'recipes',
      '/recipes/new': 'recipe_form',
      '/menus': 'menus_list',
      '/history': 'history',
    };

    let currentPage = pageMapping[location.pathname];

    if (!currentPage) {
      if (location.pathname.startsWith('/menu/')) {
        currentPage = 'menu_editor';
      } else if (location.pathname.startsWith('/recipes/') && location.pathname.endsWith('/edit')) {
        currentPage = 'recipe_form';
      }
    }

    if (currentPage && !user.tutorial_pages_seen?.includes(currentPage)) {
      setRunTutorial(true);
    }
  }, [user, location.pathname]);

  const navItems = [
    { path: '/dashboard', icon: Home, label: t('nav.dashboard') },
    { path: '/recipes', icon: ChefHat, label: t('nav.recipes') },
    { path: '/menus', icon: CalendarDays, label: t('nav.menus') },
    { path: '/training', icon: Dumbbell, label: t('nav.training') },
    { path: '/history', icon: History, label: t('nav.history') },
  ];

  const isActive = (path) => {
    if (path === '/menus') {
      return location.pathname === '/menus' || location.pathname.startsWith('/menu/');
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-cream">
      <Tutorial run={runTutorial} setRun={setRunTutorial} />
      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 font-manrope font-bold text-xl text-charcoal" data-testid="logo-link">
            <img src="/logo.svg" alt="MealPlanner" className="w-8 h-8" />
            MealPlanner
          </Link>

          <nav className="flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-manrope font-medium transition-colors ${item.path === '/recipes' ? 'tutorial-recipes-desktop' : ''} ${isActive(item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-stone hover:text-charcoal hover:bg-muted'
                    }`}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {/* Admin link */}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-manrope font-medium transition-colors ${location.pathname === '/admin'
                  ? 'bg-sage/10 text-sage'
                  : 'text-stone hover:text-sage hover:bg-sage/5'
                  }`}
                data-testid="nav-admin"
              >
                <Shield className="w-4 h-4" strokeWidth={1.5} />
                Admin
              </Link>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0 tutorial-settings-desktop" data-testid="user-menu-button">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full object-cover aspect-square" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center aspect-square">
                      <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-xl">
                <div className="px-3 py-2">
                  <p className="font-manrope font-semibold text-charcoal">{user?.name}</p>
                  <p className="font-inter text-sm text-stone break-words">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer"
                  data-testid="menu-settings"
                >
                  <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/contact')}
                  className="cursor-pointer"
                  data-testid="menu-contact"
                >
                  <MessageSquare className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('contact.title')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Top Header */}
      <header className="md:hidden sticky top-0 z-50 w-full border-b border-border/40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-14 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2 font-manrope font-bold text-lg text-charcoal">
          <img src="/logo.svg" alt="MealPlanner" className="w-6 h-6" />
          MealPlanner
        </Link>

        {/* User Menu Trigger - Reusing the same button format */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="h-9 w-9 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" strokeWidth={1.5} />
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 rounded-xl">
            <div className="px-3 py-2">
              <p className="font-manrope font-semibold text-charcoal">{user?.name}</p>
              <p className="font-inter text-sm text-stone break-words">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
              <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/contact')} className="cursor-pointer">
              <MessageSquare className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('contact.title')}
            </DropdownMenuItem>
            {user?.role === 'admin' && (
              <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-sage focus:text-sage">
                <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main Content */}
      <main className="pb-24 pt-2 md:pt-0 md:pb-8 scroll-optimize">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-t border-border p-4 flex justify-around items-center z-50 pb-safe">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 min-w-[64px] touch-target ${item.path === '/recipes' ? 'tutorial-recipes-mobile' : ''} ${isActive(item.path)
                ? 'text-primary'
                : 'text-stone'
                }`}
              data-testid={`mobile-nav-${item.path.replace('/', '')}`}
            >
              <Icon className="w-6 h-6" strokeWidth={1.5} />
              <span className="font-inter text-xs">{item.label}</span>
            </Link>
          );
        })}
        <Link
          to="/settings"
          className={`flex flex-col items-center gap-1 min-w-[64px] touch-target tutorial-settings-mobile ${location.pathname === '/settings'
            ? 'text-primary'
            : 'text-stone'
            }`}
          data-testid="mobile-nav-settings"
        >
          <Settings className="w-6 h-6" strokeWidth={1.5} />
          <span className="font-inter text-xs">{t('nav.settings')}</span>
        </Link>
      </nav>
    </div>
  );
}
